import { supabase } from '../supabase';
import type { ReportQueryConfig, ReportExecution } from '../types';

interface QueryExecutionResult {
  success: boolean;
  data?: any[];
  execution_id?: string;
  execution_time_ms: number;
  rows_returned: number;
  error_message?: string;
}

export class QueryExecutionService {
  // Execute a query configuration safely
  static async executeQuery(
    queryConfig: ReportQueryConfig,
    reportId?: string,
    executionType: 'manual' | 'scheduled' | 'preview' = 'manual'
  ): Promise<QueryExecutionResult> {
    try {
      // Call the Supabase Edge Function for secure query execution
      const { data, error } = await supabase.functions.invoke('execute-analytics-query', {
        body: {
          query_config: queryConfig,
          report_id: reportId,
          execution_type: executionType
        }
      });

      if (error) {
        throw new Error(error.message || 'Query execution failed');
      }

      return {
        success: data.success || false,
        data: data.data || [],
        execution_id: data.execution_id,
        execution_time_ms: data.execution_time_ms || 0,
        rows_returned: data.rows_returned || 0,
        error_message: data.error_message
      };

    } catch (error) {
      console.error('Query execution error:', error);
      return {
        success: false,
        execution_time_ms: 0,
        rows_returned: 0,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Validate query configuration
  static async validateQuery(queryConfig: ReportQueryConfig): Promise<{ valid: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('validate_query_config', {
        config: queryConfig
      });

      if (error) {
        return { valid: false, error: error.message };
      }

      return data || { valid: false, error: 'Validation failed' };
    } catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Validation error'
      };
    }
  }

  // Get available data sources
  static async getDataSources(): Promise<Array<{ name: string; description: string; tables: string[] }>> {
    try {
      const { data, error } = await supabase.rpc('get_available_data_sources');

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error getting data sources:', error);
      return [];
    }
  }

  // Get available fields for a data source
  static async getDataSourceFields(dataSource: string): Promise<Array<{ name: string; type: string; description?: string }>> {
    try {
      const { data, error } = await supabase.rpc('get_available_fields', {
        data_source: dataSource
      });

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error getting fields:', error);
      return [];
    }
  }

  // Preview query results (limited to 10 rows)
  static async previewQuery(queryConfig: ReportQueryConfig): Promise<any[]> {
    const limitedConfig = {
      ...queryConfig,
      limit: Math.min(queryConfig.limit || 10, 10)
    };

    const result = await this.executeQuery(limitedConfig, undefined, 'preview');
    return result.data || [];
  }

  // Cancel a running query execution
  static async cancelExecution(executionId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('report_executions')
        .update({ 
          status: 'cancelled',
          error_message: 'Cancelled by user'
        })
        .eq('id', executionId)
        .eq('status', 'running');

      return !error;
    } catch (error) {
      console.error('Error cancelling execution:', error);
      return false;
    }
  }

  // Get execution status
  static async getExecutionStatus(executionId: string): Promise<ReportExecution | null> {
    try {
      const { data, error } = await supabase
        .from('report_executions')
        .select('*')
        .eq('id', executionId)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error getting execution status:', error);
      return null;
    }
  }

  // Get recent executions for a report
  static async getReportExecutions(
    reportId?: string, 
    limit: number = 20
  ): Promise<ReportExecution[]> {
    try {
      let query = supabase
        .from('report_executions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (reportId) {
        query = query.eq('report_id', reportId);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error getting executions:', error);
      return [];
    }
  }

  // Execute query directly via RPC (fallback when Edge Function is unavailable)
  static async executeQueryDirect(
    queryConfig: ReportQueryConfig
  ): Promise<QueryExecutionResult> {
    try {
      const { data, error } = await supabase.rpc('execute_custom_query_v2', {
        query_config: queryConfig,
        user_org_id: null // Will use auth.uid() internally
      });

      if (error) {
        throw new Error(error.message);
      }

      const result = data?.[0];

      return {
        success: result?.success ?? false,
        data: result?.data ?? [],
        execution_time_ms: result?.execution_time_ms ?? 0,
        rows_returned: result?.data?.length ?? 0,
        error_message: result?.error_message
      };
    } catch (error) {
      console.error('Direct query execution error:', error);
      return {
        success: false,
        execution_time_ms: 0,
        rows_returned: 0,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Get SQL preview for debugging (without executing)
  static async getQueryPreview(queryConfig: ReportQueryConfig): Promise<string> {
    try {
      const { data, error } = await supabase.rpc('preview_custom_query', {
        query_config: queryConfig
      });

      if (error) {
        throw new Error(error.message);
      }

      return data || '';
    } catch (error) {
      console.error('Query preview error:', error);
      return error instanceof Error ? `ERROR: ${error.message}` : 'ERROR: Unknown error';
    }
  }

  // Count total results (for pagination)
  static async countQueryResults(queryConfig: ReportQueryConfig): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('count_custom_query_results', {
        query_config: queryConfig,
        user_org_id: null
      });

      if (error) {
        throw new Error(error.message);
      }

      return data ?? 0;
    } catch (error) {
      console.error('Count query error:', error);
      return 0;
    }
  }

  // Build SQL query string for debugging (read-only, not executed)
  static buildQueryString(queryConfig: ReportQueryConfig): string {
    const { select_fields, filters, order_by, limit } = queryConfig;
    
    // Build SELECT clause
    const selectClause = select_fields.map(field => {
      let fieldStr = field.field;
      
      if (field.aggregation) {
        fieldStr = `${field.aggregation.toUpperCase()}(${field.field})`;
      }
      
      if (field.alias) {
        fieldStr += ` AS "${field.alias}"`;
      }
      
      return fieldStr;
    }).join(', ');

    // Build WHERE clause
    let whereClause = '';
    if (filters.length > 0) {
      const conditions = filters.map((filter, index) => {
        let condition = '';
        
        if (index > 0) {
          condition += ` ${filter.logical_operator || 'AND'} `;
        }
        
        switch (filter.operator) {
          case 'equals':
            condition += `${filter.field} = '${filter.value}'`;
            break;
          case 'not_equals':
            condition += `${filter.field} != '${filter.value}'`;
            break;
          case 'greater_than':
            condition += `${filter.field} > '${filter.value}'`;
            break;
          case 'less_than':
            condition += `${filter.field} < '${filter.value}'`;
            break;
          case 'contains':
            condition += `${filter.field} ILIKE '%${filter.value}%'`;
            break;
          case 'is_null':
            condition += `${filter.field} IS NULL`;
            break;
          case 'is_not_null':
            condition += `${filter.field} IS NOT NULL`;
            break;
          default:
            condition += `${filter.field} = '${filter.value}'`;
        }
        
        return condition;
      });
      
      whereClause = ` WHERE ${conditions.join('')}`;
    }

    // Build ORDER BY clause
    let orderClause = '';
    if (order_by.length > 0) {
      const orderFields = order_by.map(order => 
        `${order.field} ${order.direction}`
      ).join(', ');
      orderClause = ` ORDER BY ${orderFields}`;
    }

    // Build LIMIT clause
    const limitClause = limit ? ` LIMIT ${limit}` : ' LIMIT 1000';

    // Construct final query
    const dataSource = 'deals'; // Default data source
    return `SELECT ${selectClause} FROM ${dataSource}${whereClause}${orderClause}${limitClause}`;
  }
}